import React, { useEffect, useState } from 'react';
import { Typography, Row, Col, Card, Space, Button, Divider, Modal, Form, Input, Checkbox, message } from 'antd';
import { ArrowLeftOutlined, MailOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import './SkinCancerMarketPage.css';

const { Title, Text, Paragraph } = Typography;

const SkinCancerMarketPage: React.FC = () => {
  const navigate = useNavigate();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [pageLoadTime] = useState(Date.now());
  
  const [sessionId] = useState(() => {
    const existing = localStorage.getItem('waitlist_session_id');
    if (existing) return existing;
    
    const generateId = () => {
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : ((r & 0x3) | 0x8);
        return v.toString(16);
      });
    };
    
    const newId = generateId();
    localStorage.setItem('waitlist_session_id', newId);
    return newId;
  });

  // Set page metadata
  useEffect(() => {
    document.title = 'Skin Cancer Market Intelligence | SNS Intelligence';
    
    // Update or create meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'Skin Cancer Pharmaceutical Market Intelligence - Comprehensive market analysis, pipeline tracking, and competitive intelligence for skin cancer markets from SNS Intelligence.');
  }, []);

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const handleSubmit = async (values: any) => {
    if (isSubmitting) return;
    
    message.success('You\'re on the list. We\'ll reach out when your access is approved.');
    setIsSubmitted(true);
    form.resetFields();
    setIsSubmitting(true);
    
    try {
      const hiddenFields = {
        referrer_url: document.referrer || undefined,
        landing_path: window.location.pathname,
        session_id: sessionId,
        honeypot: '',
        submit_timing: Date.now() - pageLoadTime
      };

      const response = await fetch('https://pharmadashv2-backend.onrender.com/api/waitlist', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: values.email,
          marketing_opt_in: values.marketing_opt_in,
          ...hiddenFields
        }),
      });

      if (!response.ok) {
        throw new Error('Submission failed');
      }

      const result = await response.json();
      
      if (result.status === 'accepted' || result.status === 'ok') {
        // Success - already shown message
      } else {
        throw new Error(result.message || 'Submission failed');
      }
    } catch (error) {
      console.error('Waitlist submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const validateCompanyEmail = (_: any, value: string) => {
    if (!value) {
      return Promise.reject(new Error('Please enter your email'));
    }
    
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      return Promise.reject(new Error('Please enter a valid email'));
    }
    
    // List of free email providers to reject
    const freeEmailProviders = [
      'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'live.com',
      'aol.com', 'icloud.com', 'mail.com', 'protonmail.com', 'yandex.com',
      'gmx.com', 'zoho.com', 'msn.com', 'inbox.com', 'mail.ru'
    ];
    
    const emailDomain = value.toLowerCase().split('@')[1];
    
    if (freeEmailProviders.includes(emailDomain)) {
      return Promise.reject(new Error('Please use your company email address'));
    }
    
    return Promise.resolve();
  };

  return (
    <div className="skin-cancer-market-page">
      {/* Navigation */}
      <nav className="skin-cancer-market-navbar">
        <div className="nav-container">
          <div className="nav-left">
            <div
              className="nav-logo"
              role="button"
              tabIndex={0}
              onClick={() => navigate('/')}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  navigate('/');
                }
              }}
            >
              <img src="/sns-focus-logo.png" alt="SNS Intelligence" />
            </div>
            <div className="nav-links">
              <a href="/platform">Platform</a>
              <a href="/markets">Markets</a>
              <a href="/pulse">Pulse</a>
              <a href="/press">Press</a>
            </div>
          </div>
          <div className="nav-actions">
            <Button type="primary" onClick={() => navigate('/login')}>Open Portal</Button>
          </div>
        </div>
      </nav>

      {/* Back to Home Button */}
      <div className="back-to-home-section">
        <Button
          type="default"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/')}
          className="back-to-home-button"
        >
          Back to Home
        </Button>
      </div>

      <div className="skin-cancer-market-container">
        <Row justify="center">
          <Col xs={24} md={20} lg={16}>
            <div className="skin-cancer-market-header">
              <Title level={1} className="skin-cancer-market-title">
                Skin Cancer Pharmaceutical Market Intelligence
              </Title>
            </div>

            <Card className="skin-cancer-market-card">
              <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <Paragraph className="skin-cancer-market-intro">
                  Skin cancer represents a clinically and commercially diverse oncology market characterized by heterogeneity across tumor types, disease stages, and therapeutic strategies. The market encompasses multiple distinct oncologic indications, each with unique disease biology, treatment intensity requirements, competitive dynamics, and commercial models. Market participants must navigate evolving therapeutic landscapes, assess competitive positioning across diverse treatment modalities, and evaluate how pipeline activity shapes future market dynamics in an oncology context.
                </Paragraph>
                <Paragraph>
                  SNS Intelligence structures skin cancer market analysis to support strategic decision-making for pharmaceutical, oncology, business development, and strategy professionals. The platform enables structured analysis of opportunity, competition, and pipeline evolution across skin cancer markets, accounting for the heterogeneous nature of oncologic skin malignancies and the strategic complexity inherent in oncology-focused market intelligence.
                </Paragraph>

                <div>
                  <Title level={2} className="section-title">
                    Market Scope and Therapeutic Segmentation
                  </Title>
                  <Paragraph>
                    Skin cancer markets span multiple distinct oncologic indications, each with significant differences in disease biology, treatment intensity, competitive dynamics, and commercial models. This heterogeneity means that aggregate market views can obscure critical strategic insights, making indication-level analysis essential for informed decision-making in oncology markets.
                  </Paragraph>
                  <Paragraph>
                    The platform structures skin cancer markets to reveal how therapeutic segmentation differs across indications. Representative examples include melanoma, basal cell carcinoma, and squamous cell carcinoma (these examples are not exhaustive). By enabling direct comparison of market dynamics, competitive intensity, and opportunity profiles across skin cancer indications, the platform supports identification of where strategic focus should be concentrated and where market conditions favor different approaches to development, commercialization, or partnership strategies. Detailed indication-level views are available within the platform for authenticated users, providing the granularity needed for strategic assessment in oncology markets.
                  </Paragraph>
                </div>

                <div>
                  <Title level={2} className="section-title">
                    Pipeline and Development Landscape
                  </Title>
                  <Paragraph>
                    Understanding how innovation intensity and modality diversity shape skin cancer competition is critical for strategic planning in oncology markets. The platform structures pipeline information to reveal where development activity is concentrated versus fragmented, how different developers are positioning across indications, and how emerging therapeutic approaches may reshape competitive landscapes.
                  </Paragraph>
                  <Paragraph>
                    By maintaining linkage between pipeline candidates, developers, and target indications, the platform supports evaluation of pipeline crowding versus whitespace opportunities, identification of strategic positioning patterns, and assessment of how different therapeutic approaches and development strategies influence market evolution. Users can compare pipeline activity across skin cancer indications to identify where development intensity may impact commercial prospects, where early-stage activity signals emerging opportunities, and where strategic positioning aligns with market evolution. This analytical capability informs portfolio prioritization, competitive monitoring, and business development activities in oncology-focused markets.
                  </Paragraph>
                </div>

                <div>
                  <Title level={2} className="section-title">
                    Forecast and Segmentation Analysis
                  </Title>
                  <Paragraph>
                    Multi-dimensional segmentation is critical in oncology-focused skin cancer markets because opportunity, competitive dynamics, and access considerations vary significantly across therapeutic approaches, drug classes, disease categories, and geographic markets. The platform structures forecasts and market views to support strategic analysis across these dimensions, enabling identification of where market opportunity aligns with competitive positioning and access requirements.
                  </Paragraph>
                  <Paragraph>
                    This segmentation capability supports strategic decision-making by revealing how opportunity profiles differ across dimensions—for example, how therapeutic approach dynamics affect competitive positioning, how disease category variations influence market access strategies, or how geographic variations in treatment patterns create distinct regional opportunities. By enabling analysis across therapeutic categories, drug classes, disease categories, and geographic layers, the platform supports opportunity sizing, competitive positioning, and investment prioritization decisions that account for market heterogeneity in oncology markets.
                  </Paragraph>
                </div>

                <div>
                  <Title level={2} className="section-title">
                    Competitive and Developer Landscape
                  </Title>
                  <Paragraph>
                    Competitive dynamics in skin cancer vary significantly across sub-markets, with some areas characterized by concentrated competition among established oncology leaders and others exhibiting fragmented development activity or whitespace opportunities. The platform structures competitive information to reveal where developer participation is concentrated versus distributed, how pipeline activity signals future competitive intensity, and where strategic positioning patterns create opportunities or challenges.
                  </Paragraph>
                  <Paragraph>
                    By analyzing developer participation patterns across skin cancer indications, users can identify areas where competitive crowding may impact commercial prospects, where fragmented development suggests partnership or acquisition opportunities, and where strategic positioning aligns with market evolution. The platform supports assessment of competitive positioning across therapeutic areas, development stages, and geographic markets, enabling both competitive intelligence monitoring and business development activities that account for the heterogeneous competitive landscape within skin cancer. This includes analysis of established oncology leaders, specialty developers, and emerging innovators, as well as strategic positioning, partnership activity, and pipeline-driven competition.
                  </Paragraph>
                </div>

                <div>
                  <Title level={2} className="section-title">
                    Geographic and Regional Perspectives
                  </Title>
                  <Paragraph>
                    Geographic variations in treatment adoption, access pathways, and competitive dynamics create distinct regional opportunities and challenges within skin cancer markets. The platform structures market information to support geographic analysis and market prioritization, maintaining consistent segmentation and methodology across geographic layers to enable meaningful comparison.
                  </Paragraph>
                  <Paragraph>
                    This geographic comparability supports strategic decision-making by revealing where regional market conditions favor different approaches to development, commercialization, or partnership strategies. Users can evaluate regional opportunities, assess market entry strategies, and analyze geographic variations in competitive dynamics and treatment patterns to inform prioritization of resources and strategic focus across global markets. The platform enables analysis at global, regional, and country levels, supporting both high-level strategic assessment and detailed market entry planning in oncology markets.
                  </Paragraph>
                </div>

                <div>
                  <Title level={2} className="section-title">
                    Use Cases in Skin Cancer Strategy and Planning
                  </Title>
                  <Paragraph>
                    The platform supports multiple professional use cases within skin cancer market analysis and strategic planning. Market opportunity assessment across skin cancer indications enables evaluation of market potential and competitive dynamics, supporting resource allocation and strategic focus decisions. Oncology portfolio and lifecycle planning leverages pipeline analysis and market sizing data to inform development prioritization and therapeutic area strategy.
                  </Paragraph>
                  <Paragraph>
                    Competitive intelligence and pipeline monitoring supports tracking of developer activity, pipeline developments, and market participation trends across skin cancer indications. Partnership and acquisition evaluation enables identification of collaboration opportunities and assessment of developer positioning. Regional launch and expansion strategy utilizes geographic segmentation to assess opportunities and competitive dynamics across regions and countries. Scenario analysis under different competitive and regulatory assumptions leverages segmented views to evaluate different market conditions and strategic options, supporting robust decision-making under uncertainty in oncology markets.
                  </Paragraph>
                </div>

                <div>
                  <Title level={2} className="section-title">
                    Access Skin Cancer Market Intelligence
                  </Title>
                  <Paragraph>
                    SNS Intelligence is a subscription-based platform with controlled access, intended for professional and corporate use. Access is managed through organizational subscriptions, ensuring appropriate use and data security. If you are interested in learning more about how the platform supports skin cancer market analysis and strategic decision-making, or to discuss your organization's specific analytical needs, we invite you to request access or contact our team to schedule a platform overview.
                  </Paragraph>
                  <div style={{ marginTop: '24px' }}>
                    <Space size="middle">
                      <Button 
                        type="primary" 
                        size="large"
                        onClick={showModal}
                      >
                        Request Free Trial
                      </Button>
                      <Button 
                        size="large"
                        onClick={() => {
                          navigate('/');
                          // Scroll to pricing section after navigation
                          setTimeout(() => {
                            const pricingSection = document.getElementById('pricing');
                            if (pricingSection) {
                              pricingSection.scrollIntoView({ behavior: 'smooth' });
                            } else {
                              // Fallback: try using hash
                              window.location.hash = 'pricing';
                            }
                          }, 300);
                        }}
                      >
                        View Pricing
                      </Button>
                    </Space>
                  </div>
                  <div style={{ marginTop: '32px', paddingTop: '24px', borderTop: '1px solid #e8e8e8' }}>
                    <Text style={{ color: '#595959', fontSize: '16px' }}>
                      Have questions? Contact us at{' '}
                      <a 
                        href="mailto:info@snsintel.com" 
                        style={{ 
                          color: '#1890ff', 
                          textDecoration: 'none',
                          fontWeight: 500
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.textDecoration = 'underline';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.textDecoration = 'none';
                        }}
                      >
                        info@snsintel.com
                      </a>
                    </Text>
                  </div>
                </div>
              </Space>
            </Card>
          </Col>
        </Row>
      </div>

      {/* Footer */}
      <footer className="skin-cancer-market-footer">
        <div className="section-container">
          <div className="footer-content">
            <div className="footer-logo-section">
              <div className="footer-logo">
                <img src="/sns-focus-logo.png" alt="SNS Intelligence" />
              </div>
            </div>
            <div className="footer-links-section">
              <div className="footer-column">
                <Title level={5}>Product</Title>
                <div className="footer-links">
                  <a href="/platform">Platform</a>
                  <a href="/data-coverage">Data Coverage</a>
                  <a href="/markets">Markets</a>
                  <a href="/pulse">Pulse</a>
                </div>
              </div>
              <div className="footer-column">
                <Title level={5}>Company</Title>
                <div className="footer-links">
                  <a href="/about">About</a>
                  <a href="/contact">Contact</a>
                  <a href="/press">Press</a>
                </div>
              </div>
            </div>
          </div>
          <Divider />
          <div className="footer-bottom">
            <Text>&copy; 2026 SNS Intelligence. All rights reserved.</Text>
            <div className="footer-legal">
              <a href="/privacy-policy">Privacy Policy</a>
              <a href="/terms-of-service">Terms of Service</a>
            </div>
          </div>
        </div>
      </footer>

      {/* Trial Request Modal */}
      <Modal
        title="Request Free Trial"
        open={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        width={500}
      >
        {!isSubmitted ? (
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            autoComplete="off"
          >
            <Form.Item
              name="email"
              label="Company Email"
              rules={[{ validator: validateCompanyEmail }]}
            >
              <Input 
                placeholder="your.email@company.com" 
                size="large"
                prefix={<MailOutlined />}
              />
            </Form.Item>

            <Form.Item
              name="marketing_opt_in"
              valuePropName="checked"
              rules={[{ 
                validator: (_, value) => 
                  value ? Promise.resolve() : Promise.reject(new Error('Please accept to continue'))
              }]}
            >
              <Checkbox>
                I agree to receive updates about the trial and marketing communications.
              </Checkbox>
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                size="large"
                loading={isSubmitting}
                block
                htmlType="submit"
              >
                Request Free Trial
              </Button>
            </Form.Item>
          </Form>
        ) : (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <CheckCircleOutlined style={{ fontSize: '48px', color: '#52c41a', marginBottom: '16px' }} />
            <Title level={4}>You're on the list!</Title>
            <Paragraph>
              We'll reach out when your access is approved.
            </Paragraph>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default SkinCancerMarketPage;
